import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 1 — Swara Variations Exercise (Sa Re Ga Ma Pa).
 *
 * Five fingers, one position. Eight modes — eight different sound-worlds —
 * by changing only the 2nd, 3rd and 4th degrees. The right hand never moves
 * laterally; the SCALE moves underneath it.
 */

const VARIATIONS: { id: string; name: string; description: string; intervals: [number, number, number, number, number]; vibe: string }[] = [
  { id: "major",     name: "Major",                      description: "Bright, balanced.",                                  intervals: [0, 2, 4, 5, 7], vibe: "Happy" },
  { id: "minor",     name: "Minor",                      description: "Drop the 3rd; mood collapses to pensive.",          intervals: [0, 2, 3, 5, 7], vibe: "Sad" },
  { id: "arabic",    name: "Arabic (Phrygian-Dominant)", description: "♭2 against M3 — Arabic / flamenco fingerprint.",   intervals: [0, 1, 4, 5, 7], vibe: "Eastern" },
  { id: "spanish",   name: "Spanish (Phrygian)",         description: "Both 2 and 3 lowered — flamenco country.",          intervals: [0, 1, 3, 5, 7], vibe: "Spanish" },
  { id: "gothic",    name: "Exotic / Indian (Purvi)",    description: "♭2 + M3 + ♯4 — a very Indian, raga-like sound.",    intervals: [0, 1, 4, 6, 7], vibe: "Mysterious" },
  { id: "lydian",    name: "Lydian",                     description: "♯4 — the Lydian sound; in raga terms, Yaman.",      intervals: [0, 2, 4, 6, 7], vibe: "Floating" },
  { id: "arr",       name: "A.R. Rahman",                description: "♭3 + ♯4 — a scale A.R. Rahman uses a lot.",       intervals: [0, 2, 3, 6, 7], vibe: "Carnatic" },
  { id: "wildest",   name: "♭2 ♭3 ♯4",                  description: "♭2 + ♭3 + ♯4 — the weirdest of the eight.",     intervals: [0, 1, 3, 6, 7], vibe: "Unsettled" },
];

export default function PianoIntermediate1() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "1");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Swara Variations Exercise"
          exerciseGoal="Five fingers, one position — eight modes. Train the ear to hear modal flavour by tiny interval shifts."
          bpm={80}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const [variationIdx, setVariationIdx] = useState(0);
  const variation = VARIATIONS[variationIdx];

  // Light up the five degrees of the current variation on the master keyboard
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const litHighlights: KeyHighlight[] = variation.intervals.map((semis, i) => {
    const pc = (k.tonicPc + semis) % 12;
    return {
      pc,
      tone: i === 0 ? "root" : i === 4 ? "voice" : "highlight",
      label: degreeLabel(semis),
    };
  });

  const playPattern = async () => {
    kickAudio();
    // Pattern: 1 2 3 4 5 4 3 2 — eighth notes at the drill's stated 80 BPM.
    // `time` is in seconds, so it has to come from the tempo: a hard-coded
    // 0.4 s is 75 BPM, and a student playing along with a metronome drifts.
    const BPM = 80;
    const eighth = 60 / BPM / 2;
    const pattern = [0, 1, 2, 3, 4, 3, 2, 1];
    const events = pattern.map((idx, i) => ({
      time: i * eighth,
      notes: [midiToPitchName(tonicMidi + variation.intervals[idx], k.useFlats)],
      duration: "8n",
    }));
    await playSequence(events, BPM);
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={litHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The five fingers of the current variation"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Sa (1) and Pa (5) stay locked. Re (2), Ga (3) and Ma (4) shift —
          and that's how we get eight different scales without moving your hand.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The drill" title="One hand position, eight scales">
          <p>
            Most pianists practise scales as full ascents and descents. Today
            we flip it: stay inside the first <strong>five degrees</strong> of
            the scale ("Sa Re Ga Ma Pa" = 1 2 3 4 5) and walk through eight
            different scales without ever leaving the hand position.
          </p>
          <p>
            The hand is still. The <em>sound</em> of the scale changes
            radically. This is an ear-training drill more than a finger drill.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Use your thumb for the root, use your pinky for the fifth, and
            now what else do we have? Let's start by building a simple
            movement where you go 1 2 3 4 5 4 3 2."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The pattern" title="1 2 3 4 5 4 3 2">
          <p>
            One ascending arc, one descending arc, eight notes total. Set the
            metronome at 80 BPM, eighth notes, both up and down. The same
            fingering — thumb on 1, pinky on 5 — for every variation.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playPattern}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play "{variation.name}" in {k.tonicName}
            </button>
            <span className="self-center text-[12px] italic text-cyan-300/70">
              Vibe · {variation.vibe}
            </span>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/01-swara-variations-page1.png"
          caption="Jason's chart for the eight variations — page 1."
        />
      </FadeIn>

      <FadeIn>
        <Drill label="Constellation" title="See all eight side by side">
          <p>
            Each row is one of the variations. The five dots are the five
            scale degrees (Sa Re Ga Ma Pa) plotted on a 12-semitone strip
            from tonic to fifth. Notice how every row uses the same first
            and last position (Sa stays, Pa stays) — only the middle three
            move.
          </p>
          <ModeConstellation activeIdx={variationIdx} onSelect={setVariationIdx} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The eight" title="Step through every variation">
          <p>
            Click any variation below. The master keyboard re-lights to show
            which fingers move; press <strong>play</strong> on the chip to
            hear the pattern in that mode.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            {VARIATIONS.map((v, i) => {
              const active = i === variationIdx;
              return (
                <motion.button
                  key={v.id}
                  layout
                  onClick={() => setVariationIdx(i)}
                  className={`flex items-baseline justify-between gap-3 rounded-xl border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                  }`}
                >
                  <div>
                    <div className={`text-[14px] font-semibold ${active ? "text-amber-200" : "text-cyan-100"}`}>
                      {v.name}
                    </div>
                    <div className="text-[12px] leading-snug text-cyan-300/80">
                      {v.description}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-md px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider ${
                      active ? "bg-amber-400 text-zinc-900" : "bg-cyan-500/15 text-cyan-200"
                    }`}
                  >
                    {v.vibe}
                  </span>
                </motion.button>
              );
            })}
          </div>
          <PIInsight>
            <p>
              Five-note scales train the ear faster than seven-note scales
              because the differences are concentrated in the smallest
              possible space. Once you can hear all eight, you've trained
              your ear to recognise modal flavour by interval rather than by
              song.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/01-swara-variations-page2.png"
          caption="Jason's chart — page 2."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Why these names" title="The cultural map of intervals">
          <p>
            Every variation has a fingerprint — a set of intervals that a
            culture historically built music around. The names matter less
            than the <em>sound</em> they teach you to recognise.
          </p>
          <ul className="my-3 space-y-1.5 text-[14px] text-cyan-50/95">
            <li><strong>Major / Minor</strong> — the bedrock of Western tonal music.</li>
            <li><strong>Arabic</strong> (1 ♭2 3 4 5) — the first five notes of Phrygian Dominant; a Middle-Eastern sound.</li>
            <li><strong>Spanish</strong> (1 ♭2 ♭3 4 5) — the first five notes of Phrygian.</li>
            <li><strong>Exotic / Indian</strong> (1 ♭2 3 ♯4 5) — the ♭2 and ♯4 together; a very raga-like sound (the start of Purvi).</li>
            <li><strong>Lydian</strong> (1 2 3 ♯4 5) — in raga terms, the colour of Yaman. Floating.</li>
            <li><strong>A.R. Rahman</strong> (1 2 ♭3 ♯4 5) — minor with a sharp 4; a scale Rahman uses a lot.</li>
            <li><strong>♭2 ♭3 ♯4</strong> — the weirdest the pattern can go. Try writing something on it.</li>
          </ul>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Play along" title="Hear it in the current key">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="1"
            label={`Swara variations · ${k.tonicName}`}
            originalBpm={80}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Practice ladder" title="Twelve minutes, five rungs">
          <PracticeLadder
            intro={
              <>
                The ladder walks you through four of the variations first,
                then the left-hand variation from Jason's chart at the end.
                Stay on any rung if your fingers wobble.
              </>
            }
            rungs={[
              { title: "Major (warm-up)",       minutes: 2, accent: "amber",  detail: "1 2 3 4 5 4 3 2 in major. Eighth notes at 80. Both hands or right alone." },
              { title: "Minor",                  minutes: 2, accent: "cyan",   detail: "Drop the 3rd. Same fingering. Hear the mood collapse." },
              { title: "Phrygian Dominant",      minutes: 2, accent: "rose",   detail: "1 ♭2 3 4 5. The ♭2 against the major 3rd is the hook." },
              { title: "Lydian",                 minutes: 2, accent: "emerald",detail: "1 2 3 ♯4 5. Lift the 4th by a semitone. Floating, cinematic." },
              { title: "Same right hand, floating left-hand bass", minutes: 4, accent: "violet", detail: "Keep the right hand running 1 2 3 4 5 4 3 2 in any variation and let the left hand float between bass notes underneath. Then transpose the variation you like to other keys. Make it musical — almost like a song you're making." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* —— Bespoke visual: 8-row constellation. One row per variation; each row
       has 8 cells (semitones 0–7) and the five dots fall on the variation's
       intervals. Lets the student SEE the shape of each mode at a glance. —— */
function ModeConstellation({
  activeIdx,
  onSelect,
}: {
  activeIdx: number;
  onSelect: (i: number) => void;
}) {
  return (
    <div className="my-4 overflow-x-auto rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="space-y-1">
        {VARIATIONS.map((v, vi) => {
          const active = vi === activeIdx;
          return (
            <motion.button
              key={v.id}
              layout
              onClick={() => onSelect(vi)}
              className={`flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors ${
                active ? "bg-amber-500/15 ring-1 ring-amber-300" : "hover:bg-cyan-500/10"
              }`}
            >
              <span
                className={`w-32 shrink-0 truncate text-[12px] font-semibold ${
                  active ? "text-amber-200" : "text-cyan-100/85"
                }`}
              >
                {v.name}
              </span>
              {/* 8 cells representing semitones 0..7 (tonic to fifth, inclusive) */}
              <div className="flex flex-1 items-center gap-1">
                {Array.from({ length: 8 }, (_, semi) => {
                  const isLit = v.intervals.includes(semi as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7);
                  const isAnchor = semi === 0 || semi === 7;
                  return (
                    <span
                      key={semi}
                      className={`h-5 flex-1 rounded ${
                        isLit
                          ? isAnchor
                            ? "bg-amber-400"
                            : active
                            ? "bg-cyan-300"
                            : "bg-cyan-500"
                          : "bg-zinc-800/70"
                      }`}
                    />
                  );
                })}
              </div>
              <span className="w-20 shrink-0 text-right text-[10px] uppercase tracking-wider text-cyan-300/70">
                {v.vibe}
              </span>
            </motion.button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-wider text-cyan-300/60">
        <span className="flex items-center gap-1">
          <span className="h-2 w-3 rounded bg-amber-400" /> anchors (Sa &amp; Pa)
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-3 rounded bg-cyan-500" /> the three movable degrees
        </span>
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard · live</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}

/**
 * Name a chord tone from its actual distance above the root, so a m7♭5 shows
 * "♭5" and "♭7" rather than borrowing the labels of a major 7th chord.
 */
function degreeLabel(semitonesAboveRoot: number): string {
  switch (((semitonesAboveRoot % 12) + 12) % 12) {
    case 0:  return "1";
    case 1:  return "♭2";
    case 2:  return "2";
    case 3:  return "♭3";
    case 4:  return "3";
    case 5:  return "4";
    case 6:  return "♭5";
    case 7:  return "5";
    case 8:  return "♭6";
    case 9:  return "6";
    case 10: return "♭7";
    default: return "7";
  }
}
